import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/features/auth/stores/authStore';
import { Rol } from '@/types/enums';
import { formatDateAR } from '@/utils/formatters';

import type { Protocolo } from '../types';
import { EstadoBadge } from './EstadoBadge';
import { ProtocoloListRowActions } from './ProtocoloListRowActions';

interface ProtocolosTableProps {
  items: Protocolo[];
  getProfesionalName: (uid: string) => string;
  getEstablecimientoName: (uid: string) => string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (order: 'asc' | 'desc') => void;
}

interface CardProps {
  protocolo: Protocolo;
  getProfesionalName: (uid: string) => string;
  getEstablecimientoName: (uid: string) => string;
  canManage: boolean;
  onNavigate: (uid: string) => void;
}

function ProtocoloCard({
  protocolo,
  getProfesionalName,
  getEstablecimientoName,
  canManage,
  onNavigate,
}: CardProps): JSX.Element {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
      }}
      onClick={() => onNavigate(protocolo.uid_protocolo)}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            N° {protocolo.numero_protocolo}
          </Typography>
          <EstadoBadge informeFinalEmitido={protocolo.informe_final_emitido} />
        </Stack>
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, mt: -0.5 }}>
          <ProtocoloListRowActions
            protocolo={protocolo}
            canManage={canManage}
            getEstablecimientoName={getEstablecimientoName}
            hideEnviar
          />
        </Box>
      </Stack>

      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {formatDateAR(protocolo.fecha_protocolo)}
        {' · '}
        {getProfesionalName(protocolo.uid_profesional)}
      </Typography>

      <Typography variant="body2" color="text.secondary" noWrap>
        {getEstablecimientoName(protocolo.uid_establecimiento)}
      </Typography>
    </Paper>
  );
}

export function ProtocolosTable({
  items,
  getProfesionalName,
  getEstablecimientoName,
  sortOrder,
  onSortChange,
}: ProtocolosTableProps): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentUser = useAuthStore((s) => s.currentUser);
  const canManage =
    currentUser?.rol === Rol.ADMINISTRADOR || currentUser?.rol === Rol.TECNICO_LABORATORIO;

  function handleRowClick(uid: string): void {
    navigate(`/protocolos/${uid}`);
  }

  if (isMobile) {
    return (
      <Stack spacing={1}>
        {items.map((protocolo) => (
          <ProtocoloCard
            key={protocolo.uid_protocolo}
            protocolo={protocolo}
            getProfesionalName={getProfesionalName}
            getEstablecimientoName={getEstablecimientoName}
            canManage={canManage}
            onNavigate={handleRowClick}
          />
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' } }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={sortOrder}>
              <TableSortLabel
                active
                direction={sortOrder}
                onClick={() => onSortChange(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                N° Protocolo
              </TableSortLabel>
            </TableCell>
            <TableCell>Fecha protocolo</TableCell>
            <TableCell>Recepción</TableCell>
            <TableCell>Profesional</TableCell>
            <TableCell>Establecimiento</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell align="right" sx={{ width: 200 }}>
              Acciones
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((protocolo) => (
            <TableRow
              key={protocolo.uid_protocolo}
              hover
              onClick={() => handleRowClick(protocolo.uid_protocolo)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell sx={{ fontWeight: 500 }}>{protocolo.numero_protocolo}</TableCell>
              <TableCell>{formatDateAR(protocolo.fecha_protocolo)}</TableCell>
              <TableCell>{formatDateAR(protocolo.fecha_recepcion)}</TableCell>
              <TableCell>{getProfesionalName(protocolo.uid_profesional)}</TableCell>
              <TableCell>{getEstablecimientoName(protocolo.uid_establecimiento)}</TableCell>
              <TableCell>
                <EstadoBadge informeFinalEmitido={protocolo.informe_final_emitido} />
              </TableCell>
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <ProtocoloListRowActions
                  protocolo={protocolo}
                  canManage={canManage}
                  getEstablecimientoName={getEstablecimientoName}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
